import { useEffect, useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import type { IssueCategory, IssueCategoryInput } from '../online-eval.types';

type CategoryEditorDialogProps = {
  open: boolean;
  category: IssueCategory | null;
  pending: boolean;
  error: string | null;
  onOpenChange: (open: boolean) => void;
  onSave: (input: IssueCategoryInput) => void;
};

export const CategoryEditorDialog = ({
  open,
  category,
  pending,
  error,
  onOpenChange,
  onSave,
}: CategoryEditorDialogProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (open) {
      setName(category?.name ?? '');
      setDescription(category?.description ?? '');
    }
  }, [category, open]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSave({ name, description });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>
              {category ? 'Edit issue category' : 'Create issue category'}
            </DialogTitle>
            <DialogDescription>
              Names use snake_case and are included in the evaluator schema.
              Existing findings retain this category ID when renamed.
            </DialogDescription>
          </DialogHeader>
          <label className="block space-y-2">
            <span className="text-sm font-medium">Name</span>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              pattern="[a-z][a-z0-9_]*"
              maxLength={100}
              required
              autoFocus
              placeholder="incorrect_answer"
            />
          </label>
          <label className="block space-y-2">
            <span className="text-sm font-medium">Description</span>
            <Textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={2000}
              required
              rows={6}
              placeholder="Describe what should be flagged in this category."
            />
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Saving…' : 'Save category'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
